<script lang="ts">
  import {
    type CalendarEvent,
    type CalendarDay,
    type LaneAssignable,
    CalendarCellInteraction,
    getSingleDayEvents,
    getSpanTypeClass,
    assignLanes,
    maxLaneCount,
    MAX_VISIBLE_EVENTS,
    WEEKDAY_LABELS,
    MONTH_NAMES,
    eventColor,
    eventEndDate,
    eventTotalDays,
    isMultiDayEvent,
    getTodayStr,
    formatEventTimeShort,
    hoveredDateStr,
    getWeekDates,
    cellClickMode,
    openEventModalForEdit,
  } from '../lib/calendarViewHelpers.svelte';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    year: number;
    events: CalendarEvent[];
    eventsByDate: Map<string, CalendarEvent[]>;
  }
  let { year, events, eventsByDate }: Props = $props();
  const ci = new CalendarCellInteraction(() => events);

  const COLS = 25;
  const SPAN_ROW_HEIGHT = 10;
  const DAY_HEADER_HEIGHT = 12;

  interface DayCell extends CalendarDay {
    date: Date;
    weekdayName: string;
    month: number;
    monthName: string;
    isFirstOfMonth: boolean;
    dayIndex: number;
  }
  interface SpanSegment extends LaneAssignable {
    event: CalendarEvent;
    gridRow: number;
    continuesLeft: boolean;
    continuesRight: boolean;
  }

  // ── Data computation ───────────────────────────────────────────────────────

  function computeDays(yr: number, ebd: Map<string, CalendarEvent[]>): DayCell[] {
    const result: DayCell[] = [];
    const todayStr = getTodayStr();
    const start = new Date(yr, 0, 1);
    const end = new Date(yr, 11, 31);
    const cursor = new Date(start);
    let idx = 0;
    while (true) {
      if (cursor > end) break;
      const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      const weekday = cursor.getDay();
      result.push({
        date: new Date(cursor),
        dateStr,
        day: cursor.getDate(),
        weekday,
        weekdayName: WEEKDAY_LABELS[weekday],
        month: cursor.getMonth(),
        monthName: MONTH_NAMES[cursor.getMonth()],
        isFirstOfMonth: cursor.getDate() === 1,
        isToday: dateStr === todayStr,
        isWeekend: weekday === 0 || weekday === 6,
        events: ebd.get(dateStr) || [],
        dayIndex: idx,
      });
      cursor.setDate(cursor.getDate() + 1);
      idx++;
    }
    return result;
  }

  let days = $derived(computeDays(year, eventsByDate));
  let totalRows = $derived(Math.ceil(days.length / COLS));
  let fillerCount = $derived(totalRows * COLS - days.length);

  function computeSpanSegments(
    daysArr: DayCell[],
    evts: CalendarEvent[],
  ): { segmentsByRow: Map<number, SpanSegment[]>; laneCountByRow: Map<number, number> } {
    const lookup = new Map<string, number>();
    for (const d of daysArr) lookup.set(d.dateStr, d.dayIndex);

    const yearStart = daysArr[0]?.dateStr || `${year}-01-01`;
    const yearEnd = daysArr[daysArr.length - 1]?.dateStr || `${year}-12-31`;

    const allSegments: SpanSegment[] = [];

    for (const e of evts) {
      if (!isMultiDayEvent(e)) continue;
      const eEnd = eventEndDate(e);
      if (e.date > yearEnd || eEnd < yearStart) continue;

      const startIdx = Math.max(0, lookup.get(e.date) ?? 0);
      const endDateStr = eEnd > yearEnd ? yearEnd : eEnd;
      const endIdx = Math.min(daysArr.length - 1, lookup.get(endDateStr) ?? daysArr.length - 1);
      const clippedStart = e.date < yearStart ? 0 : startIdx;
      const clippedEnd = endIdx;

      let cur = clippedStart;
      while (cur <= clippedEnd) {
        const gridRow = Math.floor(cur / COLS) + 1;
        const rowStart = (gridRow - 1) * COLS;
        const rowEnd = Math.min(rowStart + COLS - 1, clippedEnd);
        const startCol = cur - rowStart;
        const endCol = rowEnd - rowStart;

        allSegments.push({
          event: e,
          gridRow,
          startCol,
          endCol,
          lane: 0,
          continuesLeft: cur > clippedStart,
          continuesRight: rowEnd < clippedEnd,
        });

        cur = rowEnd + 1;
      }
    }

    const segmentsByRow = new Map<number, SpanSegment[]>();
    for (const seg of allSegments) {
      const arr = segmentsByRow.get(seg.gridRow) || [];
      arr.push(seg);
      segmentsByRow.set(seg.gridRow, arr);
    }

    const laneCountByRow = new Map<number, number>();
    for (const [row, segs] of segmentsByRow) {
      assignLanes(segs);
      laneCountByRow.set(row, maxLaneCount(segs));
    }

    return { segmentsByRow, laneCountByRow };
  }

  let spanData = $derived(computeSpanSegments(days, events));

  function getSpanPadding(gridRow: number): number {
    return (spanData.laneCountByRow.get(gridRow) || 0) * SPAN_ROW_HEIGHT;
  }

  function getSegmentsForRow(gridRow: number): SpanSegment[] {
    return spanData.segmentsByRow.get(gridRow) || [];
  }

  // ── Week highlight ─────────────────────────────────────────────────────────

  let weekDates = $derived($hoveredDateStr ? getWeekDates($hoveredDateStr) : new Set<string>());
</script>

<div class="big-year-container">
  <div
    class="big-year-grid"
    style="grid-template-columns: repeat({COLS}, 1fr); grid-template-rows: repeat({totalRows}, 1fr);"
  >
    {#each days as day, i}
      {@const col = (i % COLS) + 1}
      {@const row = Math.floor(i / COLS) + 1}
      {@const spanPadding = getSpanPadding(row)}
      {@const singleDayEvents = getSingleDayEvents(day.events)}
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="day-cell"
        class:today={day.isToday}
        class:weekend={day.isWeekend}
        class:first-of-month={day.isFirstOfMonth}
        class:drop-target={ci.dropTargetDate === day.dateStr}
        class:week-highlight={weekDates.has(day.dateStr)}
        class:hovered-cell={$hoveredDateStr === day.dateStr}
        style="grid-column: {col}; grid-row: {row};"
        onclick={() => {
          if ($cellClickMode === 'single') ci.cellClick(day.dateStr);
        }}
        ondblclick={() => {
          if ($cellClickMode === 'double') ci.cellClick(day.dateStr);
        }}
        onmouseenter={(e) =>
          ci.cellEnter(
            e,
            day.dateStr,
            `${day.weekdayName} ${day.monthName} ${day.day}`,
            day.events,
          )}
        onmouseleave={ci.cellLeave}
        ondragover={(e) => ci.cellDragOver(e, day.dateStr)}
        ondragleave={ci.cellDragLeave}
        ondrop={(e) => ci.cellDrop(e, day.dateStr)}
      >
        <span class="day-header">
          {#if day.isFirstOfMonth}<span class="month-label">{day.monthName}</span>{/if}
          <span class="day-weekday">{day.weekdayName}</span>
          <span class="day-number">{day.day}</span>
        </span>
        {#if spanPadding > 0}
          <div class="span-spacer" style="height: {spanPadding}px;"></div>
        {/if}
        {#if singleDayEvents.length > 0}
          <div class="event-pills">
            {#each singleDayEvents.slice(0, MAX_VISIBLE_EVENTS) as event (event.id)}
              {@const timeStr = formatEventTimeShort(event)}
              <button
                class="event-pill"
                style="border-left-color: {eventColor(event.type)}"
                class:dragging={ci.draggingEventId === event.id}
                draggable={!$viewport.isMobile}
                ondragstart={(e) => ci.pillDragStart(e, event)}
                ondragend={ci.pillDragEnd}
                onclick={(e) => {
                  e.stopPropagation();
                  openEventModalForEdit(event);
                }}
              >
                {#if timeStr}<span class="event-time">{timeStr}</span>{/if}{event.title}
              </button>
            {/each}
            {#if singleDayEvents.length > MAX_VISIBLE_EVENTS}
              <span class="event-more">+{singleDayEvents.length - MAX_VISIBLE_EVENTS} more</span>
            {/if}
          </div>
        {/if}
      </div>
    {/each}
    {#each Array.from({ length: fillerCount }, (_, i) => i) as _, fi}
      {@const idx = days.length + fi}
      {@const col = (idx % COLS) + 1}
      {@const row = Math.floor(idx / COLS) + 1}
      <div class="day-cell filler" style="grid-column: {col}; grid-row: {row};"></div>
    {/each}

    <!-- Spanning bars overlaid on the grid -->
    {#each Array.from({ length: totalRows }, (_, i) => i + 1) as gridRow}
      {@const segments = getSegmentsForRow(gridRow)}
      {#if segments.length > 0}
        {#each segments as seg (seg.event.id + '-' + gridRow + '-' + seg.startCol)}
          <button
            type="button"
            class="spanning-bar {getSpanTypeClass(seg.event.type)}"
            class:continues-left={seg.continuesLeft}
            class:continues-right={seg.continuesRight}
            class:dragging={ci.draggingEventId === seg.event.id}
            draggable={!$viewport.isMobile}
            ondragstart={(e) => ci.pillDragStart(e, seg.event)}
            ondragend={ci.pillDragEnd}
            onclick={(e) => {
              e.stopPropagation();
              ci.spanClick(seg.event);
            }}
            style="
              grid-column: {seg.startCol + 1} / span {seg.endCol - seg.startCol + 1};
              grid-row: {gridRow};
              margin-top: {DAY_HEADER_HEIGHT + seg.lane * SPAN_ROW_HEIGHT}px;
              height: {SPAN_ROW_HEIGHT - 2}px;
              align-self: start;
            "
            title="{seg.event.title} ({eventTotalDays(seg.event)} days)"
          >
            <span class="spanning-title">{seg.event.title}</span>
          </button>
        {/each}
      {/if}
    {/each}
  </div>
</div>

<style>
  @import '../lib/calendarCellStyles.css';

  .big-year-container {
    width: 100%;
    height: 100%;
    padding: 0.5rem;
    overflow: hidden;
    box-sizing: border-box;
  }

  .big-year-grid {
    display: grid;
    gap: 1px;
    background: #e5e7eb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    overflow: hidden;
    height: 100%;
  }

  .day-cell.first-of-month {
    background: #fefce8;
  }
  .day-cell.first-of-month.today {
    background: #eff6ff;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }
  .day-cell.first-of-month.week-highlight {
    background: #fef9e0;
  }

  .day-cell.filler {
    cursor: default;
    background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 3px,
      #e5e7eb 3px,
      #e5e7eb 4px
    );
    background-color: #f3f4f6;
  }

  .month-label {
    font-size: 0.5625rem;
    font-weight: 800;
    color: #b45309;
    letter-spacing: 0.02em;
    line-height: 1;
  }
  .day-header {
    display: flex;
    align-items: baseline;
    gap: 0.125rem;
    line-height: 1.1;
  }
  .day-weekday {
    font-size: 0.5rem;
    font-weight: 500;
    color: #9ca3af;
    text-transform: uppercase;
  }
  .day-number {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #374151;
  }

  /* BigYear spanning bars are placed in the CSS grid, not absolutely */
  .spanning-bar {
    position: static;
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0 3px;
    border-radius: 2px;
    font-size: 0.4375rem;
    z-index: 2;
    height: auto;
  }
  .spanning-bar.continues-left {
    padding-left: 1px;
  }
  .spanning-bar.continues-right {
    padding-right: 1px;
  }
</style>
